import {
  Active,
  DragEndEvent,
  DragOverEvent,
  DragStartEvent,
  Over,
} from '@dnd-kit/core';
import {
  DirectionalDroppableType,
  IRootWidget,
  ISortableWidgetData,
  TreeNavigatorLogic,
  Widget,
  isContainerData,
  isRoot,
} from '@framework/engine';
import produce from 'immer';
import { useCallback, useMemo, useState } from 'react';
import { useDndEditorCollisionDetection } from './useDndEditorCollisionDetection';
import { useWidgetConfigEditor } from './useWidgetConfigEditor';

export function useDndEditor(props: {
  root: IRootWidget;
  setRoot: React.Dispatch<React.SetStateAction<IRootWidget>>;
}) {
  const { root, setRoot } = props;

  const flattenWidgets = useMemo(() => {
    if (!root) return [];
    return TreeNavigatorLogic.getFlattenItems(root);
  }, [root]);

  const configEditor = useWidgetConfigEditor({
    root,
    updateRoot: setRoot,
  });

  const collisionDetection = useDndEditorCollisionDetection(root);

  /** Elemento "attivo" -> selezionato dal D'n'D */
  const [active, setActive] = useState<Widget | null>();
  /** Elemento sottostante l'elemento `active` durante il D'n'D */
  const [overId, setOverId] = useState<string | null>();

  const move = useCallback(
    (active: Active, over: Over) => {
      if (!root) {
        return;
      }

      const activeData = active.data.current as ISortableWidgetData;
      const overData = over?.data.current as ISortableWidgetData;

      // Sposto i nodi nell'albero utilizzando immerjs per rendere l'operazione immutabile
      setRoot(
        produce((root: IRootWidget) => {
          const sourceContainer = TreeNavigatorLogic.getParentNode(
            root,
            activeData,
          );

          const targetContainer = TreeNavigatorLogic.getFirstContainer(
            root,
            overData,
          );

          const sourceSiblings = sourceContainer?.children ?? [];
          const targetSiblings = targetContainer?.children ?? [];

          // Se è un container, lo metto come primo elemento, altrimenti lo sostituisco
          let position =
            isContainerData(overData) &&
            !TreeNavigatorLogic.isExtraDroppable(overData)
              ? 0
              : overData.order ?? 0;

          // Se il target è un extra-droppable `after` allora incremento la posizione di 1
          if (
            overData.directionalDroppableType === DirectionalDroppableType.After
          ) {
            position += 1;
          }

          // Controllo se i due container sono lo stesso container
          const sameContainer =
            (isRoot(sourceContainer) && isRoot(targetContainer)) ||
            (!isRoot(sourceContainer) &&
              !isRoot(targetContainer) &&
              sourceContainer?.id === targetContainer?.id);

          TreeNavigatorLogic.moveNode(
            activeData.order!,
            position,
            sourceSiblings,
            targetSiblings,
            sameContainer,
          );

          TreeNavigatorLogic.computeOrder(root);
          return root;
        }),
      );
    },
    [root, setRoot],
  );

  const removeWidget = useCallback(
    (node: Widget) => {
      setRoot(
        produce((root: IRootWidget) => {
          TreeNavigatorLogic.remove(root, node);
          TreeNavigatorLogic.computeOrder(root);
        }),
      );
    },
    [setRoot],
  );

  const insertWidget = useCallback(
    (node: Widget, target: Widget) => {
      setRoot(
        produce((root: IRootWidget) => {
          const parentWidget = TreeNavigatorLogic.getParentNode(root, target);
          // Lo inserisco prima del target
          parentWidget?.children?.splice(target.order!, 0, node);

          TreeNavigatorLogic.computeOrder(root);
        }),
      );
    },
    [setRoot],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;

      if (!active.data.current || !over?.data.current) {
        return;
      }

      if (active.id !== over?.id) {
        move(active, over);
      }

      setActive(null);
      setOverId(null);
    },
    [move],
  );

  const handleDragStart = useCallback(
    (e: DragStartEvent) => {
      setActive(flattenWidgets.find((p: any) => p.id === e.active.id) ?? null);
    },
    [flattenWidgets],
  );

  const handleDragOver = (event: DragOverEvent) => {
    if (!event.over?.data.current) {
      setOverId(null);
      return;
    }
    if (!root) return;

    setOverId(event.over.id.toString());
  };

  const handleDragCancel = useCallback(() => {
    setActive(null);
    setOverId(null);
  }, []);

  return {
    root,
    flattenWidgets,
    active,
    overId,
    move,
    handleDragEnd,
    handleDragStart,
    handleDragOver,
    handleDragCancel,
    updateWidgets: setRoot,
    configEditor,
    collisionDetection,
    removeWidget,
    insertWidget,
  };
}

export type IDndItemEditor = ReturnType<typeof useDndEditor>;
